import { ILocationFilter } from '../models/locationfilter.model';

export const EQUIPMENT_TYPES = {
  FLANGE: 'flange',
  TANK: 'tank',
  PIPE: 'pipe',
  VALVE: 'valve',
  STEAM_TRAP: 'steam trap',
  ROTATING_EQUIPMENT: 'rotating equipment',
} as const;

export class LocationFilterUtil {
  /**
   * Check if any filters are active
   */
  static isFiltering(filter: ILocationFilter): boolean {
    return (
      filter.critical ||
      filter.inactive ||
      filter.warning ||
      filter.working ||
      filter.deInstalled ||
      !!filter.customer ||
      filter.selectedTypes.length > 0 ||
      filter.location.length > filter.preselection
    );
  }

  /**
   * Check if only customer filter is active
   */
  static isOnlyFilteringCustomer(filter: ILocationFilter): boolean {
    return (
      !!filter.customer &&
      !(
        filter.critical ||
        filter.warning ||
        filter.working ||
        filter.deInstalled ||
        filter.selectedTypes.length > 0 ||
        filter.location.length > filter.preselection
      )
    );
  }

  /**
   * Get count of active filter settings
   */
  static getFilterSettingsCount(filter: ILocationFilter): number {
    return (
      (filter.critical || filter.warning || filter.working || filter.deInstalled ? 1 : 0) +
      (filter.selectedTypes.length > 0 ? 1 : 0) +
      (filter.customer ? 1 : 0) +
      Math.max(filter.location.length - filter.preselection, 0)
    );
  }

  /**
   * Create a new filter from an existing instance
   */
  static createFromInstance(instance: Partial<ILocationFilter>): ILocationFilter {
    return {
      searchTerm: instance.searchTerm ?? '',
      inactive: instance.inactive ?? false,
      critical: instance.critical ?? false,
      warning: instance.warning ?? false,
      working: instance.working ?? false,
      deInstalled: instance.deInstalled ?? false,
      selectedTypes: instance.selectedTypes ? [...instance.selectedTypes] : [],
      customer: instance.customer ?? null,
      countryOrRegionSelected: instance.countryOrRegionSelected ?? null,
      preselection: instance.preselection ?? 0,
      location: instance.location ? [...instance.location] : [],
    };
  }

  /**
   * Set equipment type filter (add or remove)
   */
  static setEquipmentType(
    filter: ILocationFilter,
    equipmentType: string,
    active: boolean
  ): ILocationFilter {
    const selectedTypes = filter.selectedTypes.filter((item) => item !== equipmentType);
    if (active) {
      selectedTypes.push(equipmentType);
    }
    return { ...filter, selectedTypes };
  }

  /**
   * Check if equipment type is selected
   */
  static hasEquipmentType(filter: ILocationFilter, equipmentType: string): boolean {
    return !!filter.selectedTypes.find((item) => item === equipmentType);
  }

  /**
   * Set Flange filter
   */
  static setFlange(filter: ILocationFilter, active: boolean): ILocationFilter {
    return this.setEquipmentType(filter, EQUIPMENT_TYPES.FLANGE, active);
  }

  /**
   * Get Flange filter status
   */
  static getFlange(filter: ILocationFilter): boolean {
    return this.hasEquipmentType(filter, EQUIPMENT_TYPES.FLANGE);
  }

  /**
   * Set Tank filter
   */
  static setTank(filter: ILocationFilter, active: boolean): ILocationFilter {
    return this.setEquipmentType(filter, EQUIPMENT_TYPES.TANK, active);
  }

  /**
   * Get Tank filter status
   */
  static getTank(filter: ILocationFilter): boolean {
    return this.hasEquipmentType(filter, EQUIPMENT_TYPES.TANK);
  }

  /**
   * Set Pipe filter
   */
  static setPipe(filter: ILocationFilter, active: boolean): ILocationFilter {
    return this.setEquipmentType(filter, EQUIPMENT_TYPES.PIPE, active);
  }

  /**
   * Get Pipe filter status
   */
  static getPipe(filter: ILocationFilter): boolean {
    return this.hasEquipmentType(filter, EQUIPMENT_TYPES.PIPE);
  }

  /**
   * Set Valve filter
   */
  static setValve(filter: ILocationFilter, active: boolean): ILocationFilter {
    return this.setEquipmentType(filter, EQUIPMENT_TYPES.VALVE, active);
  }

  /**
   * Get Valve filter status
   */
  static getValve(filter: ILocationFilter): boolean {
    return this.hasEquipmentType(filter, EQUIPMENT_TYPES.VALVE);
  }

  /**
   * Set Steam Trap filter
   */
  static setSteamTrap(filter: ILocationFilter, active: boolean): ILocationFilter {
    return this.setEquipmentType(filter, EQUIPMENT_TYPES.STEAM_TRAP, active);
  }

  /**
   * Get Steam Trap filter status
   */
  static getSteamTrap(filter: ILocationFilter): boolean {
    return this.hasEquipmentType(filter, EQUIPMENT_TYPES.STEAM_TRAP);
  }

  /**
   * Set Rotating Equipment filter
   */
  static setRotatingEquipment(filter: ILocationFilter, active: boolean): ILocationFilter {
    return this.setEquipmentType(filter, EQUIPMENT_TYPES.ROTATING_EQUIPMENT, active);
  }

  /**
   * Get Rotating Equipment filter status
   */
  static getRotatingEquipment(filter: ILocationFilter): boolean {
    return this.hasEquipmentType(filter, EQUIPMENT_TYPES.ROTATING_EQUIPMENT);
  }
}
